<template>
  <VApp :class="mainClasses">
    <Suspense>
      <AppLoader>
        <RouterView v-slot="{ Component }">
          <template v-if="Component">
            <Suspense>
              <component :is="Component" :key="Component.key" />
              <template #fallback> ... loading </template>
            </Suspense>
          </template>
          <ErrorDialog v-if="error" ref="errorDialog" :error="error" @close="removeError" />
          <VFooter v-if="showFooterBanner && !mobile" id="app-footer" class="d-flex justify-center pa-0">
            <ContentBanner class="d-flex align-self-end justify-center" :content="footerBanner" />
          </VFooter>
        </RouterView>
        <AppSnackBar ref="snack" />
        <AppSnackBar ref="errorSnack" :timeout="15000">
          <template #body>
            <VBtn class="ml-2" color="white" size="small" @click="reload">
              {{ i18n.t('generic.reloadPage') }}
            </VBtn>
          </template>
        </AppSnackBar>
      </AppLoader>
    </Suspense>
  </VApp>
</template>

<script lang="ts">
export default {
  name: 'Eurodrive'
}
</script>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { AppLoader, AppSnackBar, ErrorDialog, ContentBanner } from 'sde-client/components/shared'
import { useAuthStore, useSpacesStore, useBannerStore, useContainerStore } from 'sde-client/stores'
import { useAppStore } from 'sde-client/stores/app.store'
import { joinUrl } from 'sde-client/helpers/utils'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify/lib/framework.mjs'
import { usePreferencesStore, resetUserDataStores } from '~/stores'
import { useVersionCheck } from '~/composables/useVersionCheck'

// Exports for widgets
import * as echarts from 'echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import widgetHelper from 'sde-client/composables/widgetHelper'

// Register CanvasRenderer on echarts/core (which vue-echarts uses internally)
use([CanvasRenderer])

const i18n = useI18n()
const route = useRoute()
const router = useRouter()
const { mobile } = useDisplay()
const error = ref<undefined | Error>(undefined)
const auth = useAuthStore()
const app = useAppStore()
const spaces = useSpacesStore()
const containers = useContainerStore()
const bannerStore = useBannerStore()
const preferencesStore = usePreferencesStore()
const snack = ref()
const errorSnack = ref()

useVersionCheck()

onMounted(() => {
  // Initialize UI preferences
  preferencesStore.loadPreferences()

  window.addEventListener('unhandledrejection', (event) => {
    console.log(event)
    handleErrors(event.reason)
  })
  document.addEventListener('snack', displaySnack as EventListener)
  document.addEventListener('errorSnack', displayErrorSnack as EventListener)
})

const displaySnack = (e: CustomEvent) => {
  snack.value?.open(e.detail.text, e.detail.color, e.detail.link, e.detail.textColor)
}

const displayErrorSnack = (e: CustomEvent) => {
  errorSnack.value?.open(e.detail.text, 'error')
}

const footerBanner = computed(() => {
  return bannerStore.banner('dashboardFooter', 'dashboardFooter')
})

const showFooterBanner = computed(() => {
  return route.meta.footer && !!footerBanner.value
})

const reload = () => {
  location.reload()
}

const handleErrors = (err: Error | any) => {
  if (err.message === 'User not logged in') {
    spaces.$reset()
    containers.$reset()
    resetUserDataStores()
    auth.logout()
  } else if (err.message === 'API xhr error') {
    errorSnack.value?.open(i18n.t('generic.error408'), 'error')
    setTimeout(() => {
      location.reload()
    }, 15000)
  } else if (err.xhr?.responseJSON.errorCode === 'IMPERSONATION_ACTIVE') {
    snack.value?.open(i18n.t('users.actionPreventedDueImpersonation'), 'warning')
  } else if (err.message.includes('dynamically imported module') || err.message.includes('Unable to preload CSS for')) {
    // do nothing
  } else if (err.message) {
    error.value = err
  }
}

const removeError = () => {
  error.value = undefined
}

const mainClasses = computed(() => {
  const classes = [i18n.locale.value]
  if (auth.impersonating) {
    classes.push('impersonating')
  }
  return classes.join(' ')
})

watch(
  () => i18n.locale.value,
  (locale: string | undefined) => {
    if (locale) {
      localStorage.setItem('i18n.locale', locale)
    }
  }
)

// Router guards - role-based access control
router.afterEach(async (to) => {
  if (
    app.disclaimer?.length &&
    auth.user &&
    !auth.impersonating &&
    !auth.user.customData.disclaimerAccepted &&
    to.name !== 'disclaimer' &&
    !['login', 'resetPassword', 'setPassword'].includes(to.name as string)
  ) {
    router.push({ name: 'disclaimer' })
  }
  // Save page to localStorage
  if (to.name && !to.meta.public) {
    localStorage.setItem(auth.user.id, window.location.pathname)
  }
})

router.beforeEach(async (to) => {
  const publicPage = to.meta.public
  const adminRequired = to.meta.adminRequired
  const login = { name: 'login', query: { redirect: to.fullPath } }
  const home = { name: 'home' }
  const admin = { name: 'overview' }

  // Redirect to login if not authenticated
  if (!publicPage && !auth.user) {
    auth.setReturnUrl(to.fullPath)
    return login
  }

  // Handle disclaimer
  if (
    app.disclaimer?.length &&
    auth.user &&
    !auth.impersonating &&
    !auth.user.customData.disclaimerAccepted &&
    to.name !== 'disclaimer' &&
    !['login', 'resetPassword', 'setPassword'].includes(to.name as string)
  ) {
    return { name: 'disclaimer' }
  }

  // Redirect non-admins away from admin pages
  if (adminRequired && !auth.isAdmin) {
    return home
  }

  // Check specific permissions
  if (to.meta.permissionRequired && !auth.hasPermission(to.meta.permissionRequired as string)) {
    return admin
  }
})

router.onError((error, to) => {
  if (error.message.includes('dynamically imported module') || error.message.includes('Unable to preload CSS for')) {
    window.location = joinUrl(import.meta.env.BASE_URL, to.fullPath) as unknown as Location
  }
})

// Global application object for widgets
window.Application = {
  router,
  echarts,
  widgetHelper,
  auth
}
</script>
